import { LinkedRef, ResourceCredential, ResourcePresentation } from '../api';
import { UnknownVerifiableCredential } from './credential/verifiable/unknown';
import { VerifiableCredential } from './credential/verifiable/verifiable';
import { VerifiableCredentialWithClaims } from './credential/verifiable/with-claims';
import { VerifiablePresentation } from './presentation/issued';
import { ApiClient, BlobInput, PresentationDescriptor, UpdatePayload } from './types';
import { generateKey } from './utils/idempotency';

export class VpDecorator {
    constructor(private client: ApiClient) {}

    private linkedRefsToResourceIds(refs: LinkedRef[]): string[] {
        return refs.map((item) => item.resourceId);
    }

    private async importBlob(blob: BlobInput, request?: UpdatePayload): Promise<PresentationDescriptor> {
        const idempotencyKey = generateKey('importBlob');
        const { blobId, uploadUri } = await this.client.presentations.presentationUpload({
            idempotencyKey,
        });

        await fetch(uploadUri, {
            method: 'PUT',
            body: blob,
        });

        const resourcePresentation = await this.client.presentations.presentationImport({
            annotations: request?.annotations,
            labels: request?.labels,
            blobId,
            idempotencyKey,
        });

        return {
            id: resourcePresentation.id,
            etag: resourcePresentation.etag,
            revision: resourcePresentation.revision,
            data: {
                linkedCredentials: this.linkedRefsToResourceIds(resourcePresentation.data.linkedCredentialRefs ?? []),
            },
        };
    }

    /**
     * @deprecated Please use `importPresentation` instead. This method will be removed after January 2025.
     */
    async import(blob: BlobInput, request?: UpdatePayload): Promise<VerifiablePresentation> {
        return this.importPresentation(blob, request);
    }

    async importPresentation(blob: BlobInput, request?: UpdatePayload): Promise<VerifiablePresentation> {
        const resourcePresentation = await this.importBlob(blob, request);

        return new VerifiablePresentation(this.client, resourcePresentation);
    }

    map(input: ResourcePresentation): VerifiablePresentation {
        return new VerifiablePresentation(this.client, {
            id: input.id,
            etag: input.etag,
            revision: input.revision,
            data: {
                linkedCredentials: this.linkedRefsToResourceIds(input.data.linkedCredentialRefs ?? []),
            },
        });
    }

    async load(id: string, revision?: number): Promise<VerifiablePresentation> {
        const resourcePresentation = revision
            ? await this.client.presentations.presentationRevision(id, revision)
            : await this.client.presentations.presentationLatest(id);

        return this.map(resourcePresentation);
    }

    issue(credentials: ResourceCredential[], privateKeyId: string): Promise<VerifiablePresentation>;
    issue(credentials: VerifiableCredentialWithClaims<any>[], privateKeyId: string): Promise<VerifiablePresentation>;
    issue(credentials: UnknownVerifiableCredential<any>[], privateKeyId: string): Promise<VerifiablePresentation>;
    issue(credentials: VerifiableCredential[], privateKeyId: string): Promise<VerifiablePresentation>;
    issue(credentials: string[], privateKeyId: string): Promise<VerifiablePresentation>;
    async issue(
        credentials: string[] | VerifiableCredential[] | ResourceCredential[],
        privateKeyId: string,
    ): Promise<VerifiablePresentation> {
        const credentialIds: string[] = credentials.map((item) => {
            if (typeof item === 'string') return item;
            if (item instanceof VerifiableCredential) return item.descriptor.id;
            return item.id;
        });

        const resourcePresentation = await this.client.presentations.presentationIssue({
            credentialIds,
            keyId: privateKeyId,
            compositionType: 'EMBED',
        });

        return new VerifiablePresentation(this.client, {
            id: resourcePresentation.id,
            etag: resourcePresentation.etag,
            revision: resourcePresentation.revision,
            data: {
                linkedCredentials: this.linkedRefsToResourceIds(resourcePresentation.data.linkedCredentialRefs ?? []),
            },
        });
    }
}
